import { EntityFragment } from "@/__generated__/graphql";
import { Icon } from "@/components/atoms/Icon";
import { NodeList } from "@/components/organisms/Node/NodeList";
import { StickyLayout } from "@/components/organisms/StickyLayout";
import { useScope } from "@/hooks/useScope";
import { CommandCenterListFooter } from "@/layouts/AppLayout/CommandCenter/CommandCenterListFooter";
import { Objects } from "@/objects";
import { State } from "@/state";
import { useLayoutEffect, useRef, useState } from "react";
import { CornerDownLeftIcon, UserIcon } from "lucide-react";

export const COMMAND_ID = "select-entities";

export type SelectEntitiesContextType = {
  entityIdentifiers?: string[];
  onSubmit?: (entities: EntityFragment[]) => void;
};

function Component({
  context,
}: {
  commandInstanceId: string;
  commandDefinitionId: string;
  context?: SelectEntitiesContextType;
}) {
  const activeIndexRef = useRef<{
    data: EntityFragment | null;
    onClick: () => void;
  }>({ data: null, onClick: () => {} });
  const [transientEntityIdentifiers, setTransientEntityIdentifiers] = useState<
    string[]
  >(context?.entityIdentifiers ?? []);
  const { entities } = useScope();
  console.log({
    transientEntityIdentifiers,
    entities,
  });

  return (
    <>
      <StickyLayout
        fillHeight={false}
        stickyBottomChildren={
          <CommandCenterListFooter
            buttons={[
              {
                variant: "default",
                size: "sm",
                children: "Submit",

                onClick: () => {
                  console.log("onSubmit", transientEntityIdentifiers, entities);
                  context.onSubmit(
                    entities.filter((entity) =>
                      transientEntityIdentifiers.includes(entity.identifier),
                    ),
                  );
                },

                hotkey: "command+enter",
              },
            ]}
          />
        }
      >
        <NodeList
          {...Objects.EntityObject.getReferenceValueOptions()}
          fillHeight={false}
          isMultiselect={true}
          isCheckboxesEnabled={true}
          isNullable={false}
          onSelectMultiSelectOption={(entity: EntityFragment) => {
            setTransientEntityIdentifiers((prev) => {
              const isSelected = prev.includes(entity.identifier);
              let next;
              if (isSelected) {
                next = prev.filter(
                  (identifier) => identifier !== entity.identifier,
                );
              } else {
                next = [entity.identifier, ...prev];
              }
              // Only update if the rows differ
              if (
                next.length !== prev.length ||
                next.some((id, idx) => prev[idx] !== id)
              ) {
                return next;
              }
              return prev;
            });
          }}
          getIsChecked={(option: EntityFragment) => {
            return transientEntityIdentifiers?.includes(option.identifier);
          }}
          filterOptions={(options: EntityFragment[], filter: string) => {
            return options.filter((option) =>
              JSON.stringify(option)
                .toLowerCase()
                .includes(filter.toLowerCase()),
            );
          }}
          onActiveIndexChange={(data: EntityFragment, onClick) => {
            activeIndexRef.current.data = data;
            activeIndexRef.current.onClick = onClick;
          }}
          className="max-w-none"
        />
      </StickyLayout>
    </>
  );
}

export function SelectEntitiesCommand() {
  const registerCommandDefinition = State(
    (state) => state.registerCommandDefinition,
  );
  const unregisterCommandDefinition = State(
    (state) => state.unregisterCommandDefinition,
  );
  useLayoutEffect(() => {
    registerCommandDefinition({
      id: COMMAND_ID,
      title: "Select Entities",
      description: "Select entities to switch context...",
      getLabel: (instanceId) => {
        return "Select Entities";
      },
      onClick: () => {
        State.getState().pushToCommandStack({
          commandDefinitionId: COMMAND_ID,
          context: {
            entityIdentifiers: State.getState().activeEntityIdentifiers,
            onSubmit: (entities: EntityFragment[]) => {
              State.setState({
                activeEntityIdentifiers: entities.map(
                  (entity) => entity.identifier,
                ),
              });
              State.getState().clearCommandStack();
            },
          },
        });
      },
      ReactIcon: UserIcon,
      component: Component,
      isHidden: false,
    });

    return () => {
      unregisterCommandDefinition(COMMAND_ID);
    };
  }, []);

  return null;
}
